import { type OptimizerDetail, TradePlanDetailType } from '@/cip/_openapi/portfolio-optimizer';

import { findDetailJson, type TradeDetailRecord } from './tradeDetailsModel';

/** One row of the ACCOUNT_CONFIGURATION_INPUT snapshot (the optimizer's portfolio input frame). */
export interface AccountConfigurationInputRecord {
    pId: string;
    portfolioGuid?: string | null;
    accountGroupGuid?: string | null;
    accountType?: string | null;
    nav?: number | null;
}

/**
 * Account group guid of the account whose details are given, only when the group's growth allocation
 * was configured at portfolio level in this optimization (every row of that input frame carries the guid).
 */
export const getPortfolioLevelGroupGuid = (details: TradeDetailRecord[]): string | undefined =>
    findDetailJson<{ accountGroupGuid?: string | null }>(
        details,
        TradePlanDetailType.PORTFOLIO_GROWTH_ALLOCATION_INPUT,
    )[0]?.accountGroupGuid || undefined;

export interface AccountGroupMember {
    pId: string;
    portfolioGuid?: string;
    accountType?: string;
    nav?: number;
    details: TradeDetailRecord[];
}

/** Splits the group's detail rows per account, sorted by p_id. */
export const toAccountGroupMembers = (details: OptimizerDetail[]): AccountGroupMember[] => {
    const byPId = new Map<string, TradeDetailRecord[]>();
    for (const detail of details) {
        if (!detail.pId) continue;
        byPId.set(detail.pId, [...(byPId.get(detail.pId) ?? []), detail]);
    }

    return [...byPId.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([pId, records]) => {
            const configuration = findDetailJson<AccountConfigurationInputRecord>(
                records,
                TradePlanDetailType.ACCOUNT_CONFIGURATION_INPUT,
            )[0];
            return {
                pId,
                portfolioGuid: configuration?.portfolioGuid ?? undefined,
                accountType: configuration?.accountType ?? undefined,
                nav: configuration?.nav ?? undefined,
                details: records,
            };
        });
};
